import React from 'react';
import { toast } from 'react-toastify';
import { StatusHandleButtons } from '~/components/common/statuses/StatusHandleButtons';
import { update as updateStatus } from '~/utils/api/vendors/DealStatus';
import { isSuccess } from '~/utils/api/api_handler';
import useLoader from '~/hooks/useLoader';
import { useDeals } from '~/hooks/vendors/useDeals';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { useDealEstimationHistories } from '~/hooks/vendors/deals/useDealEstimationHistories';
import {
  updateAccept,
  updateRequest
} from '~/utils/api/vendors/deals/DealEstimationHeader';
import { IN_ESTIMATION } from '~/models/Status';
import { useDealEstimationHeaders } from '~/hooks/vendors/deals/useDealEstimationHeaders';

type Props = {
  nextStatusId: number;
  prevStatusId?: number | null;
  forwardDisabled?: boolean;
  backwardDisabled?: boolean;
};

export const StatusHandleVendorButtons: React.FC<Props> = ({
  nextStatusId,
  prevStatusId = null,
  forwardDisabled = false,
  backwardDisabled = false
}: Props) => {
  const { Loader, showLoader, hideLoader } = useLoader();
  const dealId = useIdQueryHandler();
  const { data: deal, mutate: mutateDeal } = useDeals().show(dealId);
  const { data: dealEstimationHeaders } =
    useDealEstimationHeaders().index(dealId);
  const { data: dealEstimationHistoryContext } =
    useDealEstimationHistories().index(dealId);
  if (
    deal === null ||
    dealEstimationHistoryContext === null ||
    dealEstimationHeaders === null
  ) {
    return null;
  }
  const acceptHeader =
    dealEstimationHeaders.find((header) => header.status === 'accept') ?? null;
  const estimationHeaderId =
    dealEstimationHistoryContext.dealEstimationHeaderId;
  const handleUpdateStatus = async (statusId: number): Promise<void> => {
    showLoader();
    const result = await updateStatus(deal.id, statusId);
    if (isSuccess(result)) {
      mutateDeal && (await mutateDeal());
      hideLoader();
      toast.success('更新しました');
      return;
    }
    hideLoader();
    toast.error(`エラーが発生しました。${result.message}`);
  };
  const handleForwardStatus = async (): Promise<void> => {
    // 見積作成中では見積りを申請できないので、ステータス進行で申請状態にさせる
    if (estimationHeaderId !== null && deal.status === IN_ESTIMATION) {
      const result = await updateRequest(deal.id, estimationHeaderId);
      // 初回の見積作成中から見積確認中へ移行する時のみ見積りを確定させる
      if (acceptHeader === null) {
        if (isSuccess(result)) {
          await updateAccept(deal.id, estimationHeaderId);
        } else {
          toast.error(`エラーが発生しました。${result.message}`);
        }
      }
    }
    await handleUpdateStatus(nextStatusId);
  };
  const handleBackwardStatus = async (): Promise<void> => {
    if (prevStatusId === null) return;
    await handleUpdateStatus(prevStatusId);
  };
  return (
    <>
      <Loader />
      <StatusHandleButtons
        backwardDisabled={backwardDisabled}
        canBackward={prevStatusId !== null}
        forwardDisabled={forwardDisabled}
        handleBackwardStatus={handleBackwardStatus}
        handleForwardStatus={handleForwardStatus}
      />
    </>
  );
};
